import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import LibreriaEsercizi from './LibreriaEsercizi'
import PaginaNotifiche from './PaginaNotifiche'
import TopBar from './TopBar'
import TimerBlocco, { TimerRecupero, TimerSerie } from './TimerBlocco'
import ModaleConferma from './ModaleConferma'
import ProgressiAtleta from './ProgressiAtleta'
import SessionePersonale from './SessionePersonale'
import { setAllenamentoInCorso } from './SponsorInterstitial'
import { pushSupportato, statoPermessoPush, abilitaNotifichePush, notifichePushAttive } from '../lib/pushNotifications'
import { useIndietro } from '../lib/navigazioneIndietro'
import { nomeFormato, comeLista, coloreFormato, parametriBlocco, secondiEsercizio, ripetizioniPreviste, caricoDi } from '../lib/formati'

/**
 * Intestazione di un blocco: numero, titolo, formato, parametri.
 *
 * Stava scritta in tre copie divergenti — la schermata di allenamento, la
 * sessione completata, l'anteprima nell'elenco — e sistemarne una lasciava
 * indietro le altre. Da qui in avanti è una sola.
 */
function IntestazioneBlocco({ numero, blocco, azione }) {
  const cf = coloreFormato(blocco.formato)
  const parametri = parametriBlocco(blocco)
  // Senza un nome dato dal coach il titolo è il formato: "Blocco 1" accanto a un
  // quadratino con dentro 1 non direbbe niente a nessuno.
  const titolo = blocco.nome?.trim() || nomeFormato(blocco.formato)
  const sottotitolo = blocco.nome?.trim()
    ? [nomeFormato(blocco.formato), parametri].filter(Boolean).join(' · ')
    : parametri
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border)', background: cf.tinta }}>
      <div style={{
        width: 30, height: 30, borderRadius: 9, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg)', border: `1px solid ${cf.testo}`,
        fontFamily: 'Syne', fontWeight: 800, fontSize: 14, color: cf.testo,
      }}>{numero}</div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>{titolo}</div>
        {/* Niente opacity: a 0.9 il sottotitolo scendeva sotto 4,5:1 di contrasto
            su Tabata e Circuit, cioè sotto la soglia per il testo piccolo. */}
        {sottotitolo && <div style={{ fontSize: 12, color: cf.testo, marginTop: 2 }}>{sottotitolo}</div>}
      </div>
      {azione}
    </div>
  )
}

/**
 * Card di un blocco: contorno visibile e costa del colore del formato.
 *
 * Il bordo è il vero separatore. Alzare il grigio del riempimento non funziona:
 * fra card e sfondo si arriva al massimo a 1,3:1 — anche con lo sfondo nero puro —
 * e già a #232733 il grigio degli esercizi scende sotto 4,5:1 di contrasto. Il
 * bordo a 0,20 di bianco stacca a 2,37:1 e disegna il contorno, che è come
 * l'occhio trova i confini. Le righe interne restano al 7%: fuori forte, dentro
 * leggero.
 */
function stileCardBlocco(formato) {
  return {
    background: 'var(--surface2)',
    border: '1px solid rgba(255,255,255,0.20)',
    borderLeft: `5px solid ${coloreFormato(formato).testo}`,
  }
}

export default function HomeAtleta({ onCambiaTab, tabAttivo = 'home', resetKey = 0 }) {
  const { profilo, esci } = useAuth()
  const [tab, setTab] = useState('oggi')
  const [sessioneOggi, setSessioneOggi] = useState(null)
  const [risultatoAperto, setRisultatoAperto] = useState(null)
  const [sessPersonali, setSessPersonali] = useState([])
  const [tracking, setTracking] = useState([])
  const [messaggi, setMessaggi] = useState([])
  const [gruppi, setGruppi] = useState([])
  const [gruppiDettaglio, setGruppiDettaglio] = useState([])
  const [gruppiCreatiCount, setGruppiCreatiCount] = useState(0) // gruppi creati dal coach
  const [caricamento, setCaricamento] = useState(true)
  const [toast, setToast] = useState('')
  const [mostraLibreria, setMostraLibreria] = useState(false)
  const [logData, setLogData] = useState({})
  const [ultimiCarichi, setUltimiCarichi] = useState({})
  const [videoPersonali, setVideoPersonali] = useState({}) // {esercizioId: link_video_personale}, mostrati sulla card durante l'esecuzione
  const [notificheNonLette, setNotificheNonLette] = useState(0)
  // Realtime badge campanella — aggiorna subito quando arriva una notifica
  useEffect(() => {
    if (!profilo?.id) return
    // Conta iniziale
    supabase.from('notifiche').select('*', { count: 'exact', head: true })
      .eq('utente_id', profilo.id).eq('letta', false)
      .then(({ count }) => setNotificheNonLette(count || 0))
    // Realtime: aggiorna badge subito su INSERT o UPDATE
    const channel = supabase
      .channel('home_badge_' + profilo.id)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifiche', filter: `utente_id=eq.${profilo.id}` },
        () => supabase.from('notifiche').select('*', { count: 'exact', head: true })
          .eq('utente_id', profilo.id).eq('letta', false)
          .then(({ count }) => setNotificheNonLette(count || 0))
      )
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'notifiche', filter: `utente_id=eq.${profilo.id}` },
        () => supabase.from('notifiche').select('*', { count: 'exact', head: true })
          .eq('utente_id', profilo.id).eq('letta', false)
          .then(({ count }) => setNotificheNonLette(count || 0))
      )
      .subscribe()
    // Espone funzione globale per ricalcolare il badge dalla PaginaNotifiche.
    // Non azzera direttamente: un invito gruppo ancora in sospeso deve restare conteggiato.
    window.__homeBadgeAggiorna = () => supabase.from('notifiche').select('*', { count: 'exact', head: true })
      .eq('utente_id', profilo.id).eq('letta', false)
      .then(({ count }) => setNotificheNonLette(count || 0))
    return () => {
      supabase.removeChannel(channel)
      delete window.__homeBadgeAggiorna
    }
  }, [profilo?.id])
  const [mostraNotifiche, setMostraNotifiche] = useState(false) // esercizio_id -> { peso, elastico, ripetizioni, durata_secondi }
  const [progressiExSel, setProgressiExSel] = useState(null)
  const [vistaProgressi, setVistaProgressi] = useState(false)  // esercizio aperto nella vista progressi
  const [progressiStorico, setProgressiStorico] = useState([]) // tutti i record di quell'esercizio
  const [datiGrafico, setDatiGrafico] = useState([])
  const [gruppoDettaglioSel, setGruppoDettaglioSel] = useState(null)
  const [sessioniGruppo, setSessioniGruppo] = useState({ storico: [], prossime: [] })
  const [caricamentoGruppo, setCaricamentoGruppo] = useState(false)
  const [vistaGruppi, setVistaGruppi] = useState(false)
  const [invitiPending, setInvitiPending] = useState([]) // inviti gruppo in attesa
  const [tabGruppo, setTabGruppo] = useState('prossime')
  const [vistaSessioneGruppo, setVistaSessioneGruppo] = useState(false)
  const [exCompletati, setExCompletati] = useState(new Set()) // logKey degli esercizi già salvati
  const [conferma, setConferma] = useState(null)             // conferma generica (sostituisce window.confirm)
  const [timerBlocco, setTimerBlocco] = useState(null)        // blocco per cui è aperto il timer a schermo intero
  const [recuperoAttivo, setRecuperoAttivo] = useState(null)  // { logKey, secondi } countdown di recupero nel formato Classico
  const [timerSerie, setTimerSerie] = useState(null)          // serie a tempo in esecuzione: a fine countdown si salva da sé
  // Serie in corso di salvataggio (logKey): il pulsante si disabilita.
  // Senza questo, due tocchi rapidi sul ✓ — normali con il fiato corto —
  // producevano due righe con lo stesso serie_numero, e il tetto delle serie
  // previste si poteva superare perché il conteggio veniva da uno stato
  // non ancora aggiornato.
  const [salvandoSerie, setSalvandoSerie] = useState(null)
  const [prossimeSessioniTutti, setProssimeSessioniTutti] = useState([])
  const [storicoSessioniTutti, setStoricoSessioniTutti] = useState([])
  const [prossimeSessioniCoach, setProssimeSessioniCoach] = useState([])
  const [storicoSessioniCoach, setStoricoSessioniCoach] = useState([])
  const [serieSalvate, setSerieSalvate] = useState([])
  const [serieInModifica, setSerieInModifica] = useState(null)
  const [serieEditData, setSerieEditData] = useState({})
  // Storico espandibile
  const [storGruppoSerie, setStorGruppoSerie] = useState([])      // serie della sessione aperta
  const [sessioneDettaglio, setSessioneDettaglio] = useState(null) // sessione gruppo aperta a tutto schermo
  const [storGruppoMod, setStorGruppoMod] = useState(null)        // id serie in modifica
  const [storGruppoEdit, setStorGruppoEdit] = useState({})
  // Sessione personale — il flusso vive in SessionePersonale.js, qui restano solo
  // gli stati leggeri per montarlo e dirgli come aprirsi.
  const [mostraSessPersonale, setMostraSessPersonale] = useState(false)
  const [richiestaSessPersonale, setRichiestaSessPersonale] = useState(null) // { tipo: 'nuova'|'storico'|'riprendi'|'rapido', sessione?, ex? }
  const [richiestaSessPersonaleSeq, setRichiestaSessPersonaleSeq] = useState(0) // forza il remount quando si apre una nuova richiesta
  const [sessPersonaleEsecuzioneAttiva, setSessPersonaleEsecuzioneAttiva] = useState(false) // per sopprimere lo sponsor interstitial
  const [eserciziFavoriti, setEserciziFavoriti] = useState([]) // esercizi monitorati dall'atleta
  const [pwaPrompt, setPwaPrompt] = useState(null)            // evento beforeinstallprompt
  const [pwaDismissed, setPwaDismissed] = useState(() => localStorage.getItem('pwa_dismissed') === '1')
  const [notifPushDismissed, setNotifPushDismissed] = useState(() => localStorage.getItem('notif_push_dismissed') === '1')
  const [notifPushAttivo, setNotifPushAttivo] = useState(true) // true finché non verificato, per non far comparire il banner a vuoto durante il check

  useEffect(() => { caricaDati() }, [])

  // ── CHIUSURE CONDIVISE: freccia dell'app e tasto indietro usano le stesse funzioni ──

  // Sessione di gruppo: se è in corso chiede conferma prima di uscire.
  // Restituisce false quando mostra l'avviso, così il tasto indietro non chiude la vista.
  function indietroSessioneGruppo() {
    const esciDaSessione = () => {
      setVistaSessioneGruppo(false)
      setSessioneOggi(null)
      setRisultatoAperto(null)
      setSerieSalvate([])
      setLogData({})
    }
    if (risultatoAperto && !risultatoAperto.completata) {
      setConferma({
        titolo: 'Sessione in corso',
        testo: 'Se esci adesso la sessione resta aperta e non verrà segnata come completata. Le serie già salvate restano registrate.',
        testoConferma: 'Esci comunque', testoAnnulla: 'Torna alla sessione',
        onConferma: () => { setConferma(null); esciDaSessione() },
      })
      return false
    }
    esciDaSessione()
  }

  // Tasto indietro di Android: esegue la stessa chiusura della freccia dell'app.
  // Ordine di dichiarazione = ordine di annidamento: i livelli interni dopo quelli esterni.
  useIndietro(vistaGruppi, () => { setGruppoDettaglioSel(null); setVistaGruppi(false); if (onCambiaTab) onCambiaTab('home') })
  useIndietro(!!gruppoDettaglioSel, () => setGruppoDettaglioSel(null))
  useIndietro(vistaProgressi, () => { setVistaProgressi(false); setProgressiExSel(null); setLogData({}); if (onCambiaTab) onCambiaTab('home') })
  useIndietro(!!progressiExSel, () => { setProgressiExSel(null); setLogData({}) })
  useIndietro(vistaSessioneGruppo, indietroSessioneGruppo)
  useIndietro(!!sessioneDettaglio, () => { setSessioneDettaglio(null); setStorGruppoSerie([]) })
  useIndietro(mostraNotifiche, () => { setMostraNotifiche(false); caricaDati(); window.__topBarAggiorna?.() })
  useIndietro(mostraLibreria, () => setMostraLibreria(false))
  // La sessione personale registra da sé i propri livelli (dipendono dallo step interno)

  // Verifica se le notifiche push sono già attive su questo dispositivo,
  // per decidere se mostrare il banner di invito ad attivarle
  useEffect(() => {
    if (!pushSupportato()) { setNotifPushAttivo(true); return } // niente banner se il browser non le supporta
    notifichePushAttive().then(setNotifPushAttivo)
  }, [])

  // Carica i link video personali (esercizi_preferiti) per mostrarli sulla card durante l'esecuzione
  useEffect(() => {
    async function caricaVideoPersonali() {
      const { data } = await supabase.from('esercizi_preferiti')
        .select('esercizio_id, link_video_personale')
        .eq('utente_id', profilo.id)
        .not('link_video_personale', 'is', null)
      const mappa = {}
      ;(data || []).forEach(p => { if (p.link_video_personale) mappa[p.esercizio_id] = p.link_video_personale })
      setVideoPersonali(mappa)
    }
    caricaVideoPersonali()
  }, [profilo.id])

  // Segnala che c'è un allenamento in corso, così lo sponsor a schermo intero
  // non compare in mezzo a una serie (vedi SponsorInterstitial.js).
  useEffect(() => {
    const inAllenamento = !!timerBlocco
      || (vistaSessioneGruppo && !!risultatoAperto && !risultatoAperto.completata)
      || sessPersonaleEsecuzioneAttiva
    setAllenamentoInCorso(inAllenamento)
    return () => setAllenamentoInCorso(false)
  }, [timerBlocco, vistaSessioneGruppo, risultatoAperto, sessPersonaleEsecuzioneAttiva])

  // Apre la sessione personale con una richiesta specifica (nuova, storico, riprendi, rapido).
  // Ogni apertura ha una key diversa: SessionePersonale viene montato da zero ogni volta,
  // così il suo stato interno riparte pulito senza bisogno di reset manuali.
  function apriSessPersonale(richiesta) {
    setRichiestaSessPersonale(richiesta)
    setRichiestaSessPersonaleSeq(n => n + 1)
    setMostraSessPersonale(true)
  }


  // Cattura evento installazione PWA
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setPwaPrompt(e) }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  useEffect(() => {
    if (tabAttivo === 'gruppiatleta') {
      setVistaGruppi(true); setVistaProgressi(false); setMostraSessPersonale(false); setVistaSessioneGruppo(false); setSessioneDettaglio(null)
    } else if (tabAttivo === 'progressiatleta') {
      setVistaProgressi(true); setVistaGruppi(false); setMostraSessPersonale(false); setVistaSessioneGruppo(false); setSessioneDettaglio(null)
    } else if (tabAttivo === 'home') {
      const rapido = sessionStorage.getItem('fp_avvio_rapido')
      if (rapido) {
        sessionStorage.removeItem('fp_avvio_rapido')
        try {
          const ex = JSON.parse(rapido)
          setVistaGruppi(false); setVistaProgressi(false); setVistaSessioneGruppo(false); setSessioneDettaglio(null)
          apriSessPersonale({ tipo: 'rapido', ex })
          return
        } catch {}
      }
      setVistaGruppi(false); setVistaProgressi(false); setMostraSessPersonale(false); setVistaSessioneGruppo(false); setSessioneDettaglio(null)
    }
  }, [tabAttivo, resetKey])


  async function rispondiInvitoGruppo(membroId, gruppoId, risposta) {
    const { error } = await supabase.from('gruppo_membri')
      .update({ stato: risposta === 'accetta' ? 'attivo' : 'rifiutato' })
      .eq('id', membroId)
    // Senza questo controllo l'invito restava in lista e all'atleta non veniva
    // detto niente: sembrava che il tocco non avesse funzionato.
    if (error) {
      toast_msg(error.message?.includes('LIMITE_PIANO_FREE_MEMBRI')
        ? 'Il gruppo ha raggiunto il numero massimo di atleti: chiedi al coach'
        : 'Risposta non registrata: ' + error.message)
      return
    }
    if (risposta === 'accetta') {
      // Notifica al coach
      const { data: m } = await supabase.from('gruppo_membri')
        .select('gruppi(gestore_id, nome)').eq('id', membroId).maybeSingle()
      if (m?.gruppi?.gestore_id) {
        await supabase.from('notifiche').insert({
          utente_id: m.gruppi.gestore_id,
          tipo: 'info',
          titolo: '✅ Invito accettato',
          testo: `${profilo.nome} ${profilo.cognome || ''} ha accettato l'invito nel gruppo "${m.gruppi.nome}".`,
        })
      }
    }
    caricaDati()
  }

  async function caricaDettaglioGruppo(gruppo) {
    setGruppoDettaglioSel(gruppo)
    setCaricamentoGruppo(true)
    const oggi2 = new Date().toISOString().split('T')[0]

    const [{ data: prossData }, { data: storData }] = await Promise.all([
      supabase.from('sessioni_calendario')
        .select('*, sessioni_tipo(nome, durata_minuti, blocchi)')
        .eq('gruppo_id', gruppo.id).eq('archiviata', false).gte('data', oggi2)
        .order('data', { ascending: true }).limit(10),
      supabase.from('sessioni_calendario')
        .select('*, sessioni_tipo(nome, durata_minuti, blocchi)')
        .eq('gruppo_id', gruppo.id).lt('data', oggi2)
        .order('data', { ascending: false }).limit(20),
    ])

    // Carica risultati dell'atleta per TUTTE le sessioni (prossime + storico)
    const tuttiIds = [...(prossData || []), ...(storData || [])].map(s => s.id)
    let risultatiMap = {}
    if (tuttiIds.length > 0) {
      const { data: ris } = await supabase.from('risultati_sessione')
        .select('id, completata, sessione_calendario_id')
        .eq('atleta_id', profilo.id)
        .in('sessione_calendario_id', tuttiIds)
      ;(ris || []).forEach(r => { risultatiMap[r.sessione_calendario_id] = r })
    }

    // Le sessioni "prossime" di oggi già completate vanno nello storico
    const prossimeFiltrate = (prossData || []).filter(s => {
      if (s.data !== oggi2) return true          // sessioni future: sempre in prossime
      return !risultatiMap[s.id]?.completata      // oggi: in prossime solo se NON completata
    })

    const storicoOggiCompletate = (prossData || []).filter(s =>
      s.data === oggi2 && risultatiMap[s.id]?.completata
    )

    setSessioniGruppo({
      prossime: prossimeFiltrate.map(s => ({ ...s, mioRisultato: risultatiMap[s.id] || null })),
      storico: [
        ...storicoOggiCompletate.map(s => ({ ...s, mioRisultato: risultatiMap[s.id] || null })),
        ...(storData || []).map(s => ({ ...s, mioRisultato: risultatiMap[s.id] || null })),
      ]
    })
    setCaricamentoGruppo(false)
  }

  async function caricaDati() {
    setCaricamento(true)
    const oggi = new Date().toISOString().split('T')[0]

    // Trova i gruppi a cui appartiene come membro
    const { data: memberships } = await supabase.from('gruppo_membri').select('gruppo_id').eq('utente_id', profilo.id).eq('stato', 'attivo')
    const gruppoIds = memberships?.map(m => m.gruppo_id) || []
    setGruppi(gruppoIds)
    // Carica inviti pending
    const { data: pending } = await supabase.from('gruppo_membri')
      .select('*, gruppi(nome, profili!gruppi_gestore_id_fkey(nome, cognome))')
      .eq('utente_id', profilo.id).eq('stato', 'pending')
    setInvitiPending(pending || [])
    // Conta anche i gruppi creati (solo per coach)
    if (profilo.piano === 'coach') {
      const { count } = await supabase.from('gruppi').select('id', { count: 'exact', head: true }).eq('gestore_id', profilo.id).eq('attivo', true)
      setGruppiCreatiCount(count || 0)

      // Carica sessioni assegnate ai gruppi creati dal coach (prossime e storico)
      const { data: mieiGruppi } = await supabase.from('gruppi').select('id, nome').eq('gestore_id', profilo.id).eq('attivo', true)
      const idsMieiGruppi = (mieiGruppi || []).map(g => g.id)
      if (idsMieiGruppi.length > 0) {
        const mappaNomi = Object.fromEntries((mieiGruppi || []).map(g => [g.id, g.nome]))
        const [{ data: prossCoach }, { data: storCoach }] = await Promise.all([
          supabase.from('sessioni_calendario')
            .select('*, sessioni_tipo(nome, durata_minuti, blocchi)')
            .in('gruppo_id', idsMieiGruppi).eq('archiviata', false).gte('data', oggi)
            .order('data', { ascending: true }).limit(10),
          supabase.from('sessioni_calendario')
            .select('*, sessioni_tipo(nome, durata_minuti, blocchi)')
            .in('gruppo_id', idsMieiGruppi).eq('archiviata', false).lt('data', oggi)
            .order('data', { ascending: false }).limit(10),
        ])
        setProssimeSessioniCoach((prossCoach || []).map(s => ({ ...s, nomeGruppo: mappaNomi[s.gruppo_id] || '' })))
        setStoricoSessioniCoach((storCoach || []).map(s => ({ ...s, nomeGruppo: mappaNomi[s.gruppo_id] || '' })))
      } else {
        setProssimeSessioniCoach([])
        setStoricoSessioniCoach([])
      }
    }

    // Carica dettagli gruppi (nome + coach)
    if (gruppoIds.length > 0) {
      const { data: dettagliGruppi } = await supabase
        .from('gruppi')
        .select('id, nome, gestore_id, profili!gruppi_gestore_id_fkey(nome, cognome)')
        .in('id', gruppoIds)
      setGruppiDettaglio(dettagliGruppi || [])

      // Prossime sessioni di TUTTI i gruppi
      const { data: prossimeTutte } = await supabase
        .from('sessioni_calendario')
        .select('*, sessioni_tipo(nome, durata_minuti, blocchi)')
        .in('gruppo_id', gruppoIds).eq('archiviata', false)
        .gte('data', oggi)
        .order('data', { ascending: true })
        .limit(20)
      const prossimeIds = (prossimeTutte || []).map(s => s.id)
      let prossimeCompletateMap = {}
      if (prossimeIds.length > 0) {
        const { data: pRis } = await supabase
          .from('risultati_sessione')
          .select('sessione_calendario_id, completata')
          .eq('atleta_id', profilo.id)
          .in('sessione_calendario_id', prossimeIds)
        ;(pRis || []).forEach(r => { prossimeCompletateMap[r.sessione_calendario_id] = r })
      }
      setProssimeSessioniTutti((prossimeTutte || [])
        .filter(s => !prossimeCompletateMap[s.id]?.completata)
        .map(s => ({ ...s, nomeGruppo: (dettagliGruppi || []).find(g => g.id === s.gruppo_id)?.nome || '' })))

      // Storico sessioni di TUTTI i gruppi
      const { data: storicoTutte } = await supabase
        .from('sessioni_calendario')
        .select('*, sessioni_tipo(nome, durata_minuti, blocchi)')
        .in('gruppo_id', gruppoIds)
        .lt('data', oggi)
        .order('data', { ascending: false })
        .limit(20)
      const storicoIds = (storicoTutte || []).map(s => s.id)
      let risultatiStoricoMap = {}
      if (storicoIds.length > 0) {
        const { data: sRis } = await supabase
          .from('risultati_sessione')
          .select('id, completata, sessione_calendario_id')
          .eq('atleta_id', profilo.id)
          .in('sessione_calendario_id', storicoIds)
        ;(sRis || []).forEach(r => { risultatiStoricoMap[r.sessione_calendario_id] = r })
      }
      const oggiCompletate = (prossimeTutte || [])
        .filter(s => prossimeCompletateMap[s.id]?.completata)
        .map(s => ({ ...s, nomeGruppo: (dettagliGruppi || []).find(g => g.id === s.gruppo_id)?.nome || '', mioRisultato: prossimeCompletateMap[s.id] || null }))
      setStoricoSessioniTutti([
        ...oggiCompletate,
        ...(storicoTutte || []).map(s => ({ ...s, nomeGruppo: (dettagliGruppi || []).find(g => g.id === s.gruppo_id)?.nome || '', mioRisultato: risultatiStoricoMap[s.id] || null }))
      ])
    }

    // Sessione di oggi
    if (gruppoIds.length > 0) {
      const { data: calOggi } = await supabase.from('sessioni_calendario')
        .select('*, sessioni_tipo(*)')
        .in('gruppo_id', gruppoIds).eq('archiviata', false)
        .eq('data', oggi)
        .limit(1)
        .maybeSingle()

      if (calOggi) {
        // Ricarica varianti aggiornate dal DB
        const blocchiRaw = calOggi.sessioni_tipo?.blocchi || []
        const exIds = blocchiRaw.flatMap(b => b.esercizi || []).map(e => e.esercizioId).filter(Boolean)
        let variantiMap = {}
        if (exIds.length > 0) {
          const { data: esDb } = await supabase.from('esercizi_db').select('id, varianti, regressioni').in('id', exIds)
          if (esDb) esDb.forEach(e => { variantiMap[e.id] = [...comeLista(e.varianti), ...comeLista(e.regressioni)] })
        }
        // Aggiorna i blocchi con le varianti fresche dal DB
        const blocchiAggiornati = blocchiRaw.map(b => ({
          ...b,
          esercizi: (b.esercizi || []).map(ex => ({
            ...ex,
            varianti: variantiMap[ex.esercizioId] || ex.varianti || []
          }))
        }))
        setSessioneOggi({ ...calOggi, sessioni_tipo: { ...calOggi.sessioni_tipo, blocchi: blocchiAggiornati } })
        const { data: ris } = await supabase.from('risultati_sessione')
          .select('*')
          .eq('sessione_calendario_id', calOggi.id)
          .eq('atleta_id', profilo.id)
          .maybeSingle()
        if (ris) { setRisultatoAperto(ris) }
        caricaUltimiCarichi(exIds)
      }
    }

    // Sessioni personali recenti
    const { data: sp } = await supabase.from('sessioni_personali')
      .select('*').eq('atleta_id', profilo.id)
      .order('data', { ascending: false }).limit(10)
    setSessPersonali(sp || [])

    // Tracking recente
    const { data: tr } = await supabase.from('tracking_esercizio')
      .select('*, esercizi_db(nome, tipo_misura)')
      .eq('atleta_id', profilo.id)
      .order('data', { ascending: false }).limit(100)
    setTracking(tr || [])

    // Esercizi favoriti/monitorati (quelli che l'atleta ha già trackato almeno 1 volta)
    const esUnici = []
    const visti = new Set()
    ;(tr || []).forEach(t => {
      if (!visti.has(t.esercizio_id)) {
        visti.add(t.esercizio_id)
        esUnici.push({ id: t.esercizio_id, nome: t.esercizi_db?.nome || '?', tipo_misura: t.esercizi_db?.tipo_misura || 'ripetizioni' })
      }
    })
    setEserciziFavoriti(esUnici)

    // Messaggi — fix per gruppoIds vuoto
    let msg = []
    if (gruppoIds.length > 0) {
      const { data } = await supabase.from('messaggi')
        .select('*, profili!messaggi_mittente_id_fkey(nome,cognome)')
        .or(`destinatario_id.eq.${profilo.id},gruppo_id.in.(${gruppoIds.join(',')})`)
        .order('creato_il', { ascending: false }).limit(15)
      msg = data || []
    } else {
      const { data } = await supabase.from('messaggi')
        .select('*, profili!messaggi_mittente_id_fkey(nome,cognome)')
        .eq('destinatario_id', profilo.id)
        .order('creato_il', { ascending: false }).limit(15)
      msg = data || []
    }
    setMessaggi(msg)

    // Conta notifiche non lette
    const { count } = await supabase.from('notifiche').select('*', { count: 'exact', head: true }).eq('utente_id', profilo.id).eq('letta', false)
    setNotificheNonLette(count || 0)

    setCaricamento(false)
  }

  async function caricaUltimiCarichi(esercizioIds) {
    if (!esercizioIds || esercizioIds.length === 0) return
    const { data } = await supabase
      .from('tracking_esercizio')
      .select('esercizio_id, peso, elastico, ripetizioni, durata_secondi, creato_il')
      .eq('atleta_id', profilo.id)
      .in('esercizio_id', esercizioIds)
      .order('creato_il', { ascending: false })
    if (!data) return
    const map = {}
    data.forEach(r => {
      if (!map[r.esercizio_id]) map[r.esercizio_id] = r
    })
    setUltimiCarichi(map)
  }

  async function apriEsercizioProgressi(ex) {
    setProgressiExSel(ex)
    setVistaProgressi(true)
    caricaUltimiCarichi([ex.id])
    caricaDatiGrafico(ex.id)
    // Carica tutto lo storico di questo esercizio
    const { data } = await supabase.from('tracking_esercizio')
      .select('*')
      .eq('atleta_id', profilo.id)
      .eq('esercizio_id', ex.id)
      .order('data', { ascending: false })
      .limit(30)
    setProgressiStorico(data || [])
  }

  async function caricaDatiGrafico(esercizioId) {
    const { data } = await supabase
      .from('tracking_esercizio')
      .select('data, ripetizioni, durata_secondi, peso, elastico')
      .eq('atleta_id', profilo.id)
      .eq('esercizio_id', esercizioId)
      .order('data', { ascending: true })
    if (!data) return
    setDatiGrafico(data.map(r => ({
      // 'T00:00:00': senza, la data viene letta come mezzanotte UTC e a ovest di
      // Greenwich il punto sul grafico si sposta al giorno prima.
      // "21/09": "21 set" si leggeva come "21 serie".
      data: new Date(r.data + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
      rip: r.ripetizioni || r.durata_secondi || null,
      kg: r.elastico ? null : (r.peso || 0),
      elastico: r.elastico || null,
    })))
  }

  async function iniziaSessione() {
    if (!sessioneOggi) return
    const { data, error } = await supabase.from('risultati_sessione').insert({
      sessione_calendario_id: sessioneOggi.id,
      atleta_id: profilo.id,
      iniziata_il: new Date().toISOString(),
    }).select().single()
    if (error) { toast_msg('Errore: ' + error.message); return }
    setRisultatoAperto(data)
    toast_msg('💪 Sessione iniziata!')
  }

  async function completaSessione() {
    if (!risultatoAperto) return
    const risId = risultatoAperto.id
    const { error } = await supabase.from('risultati_sessione')
      .update({ completata: true, completata_il: new Date().toISOString() }).eq('id', risId)
    // Prima l'esito non veniva guardato: compariva "🎉 Sessione completata!" e
    // il badge verde anche quando il database aveva rifiutato, e la sessione
    // risultava ancora da fare al caricamento successivo.
    if (error) { toast_msg('Sessione NON completata: ' + error.message); return }
    setRisultatoAperto(r => ({ ...r, completata: true }))
    toast_msg('🎉 Sessione completata!')
    await caricaDati()
    await caricaSerieSalvate(risId)
  }

  /** Serie già registrate per un esercizio dentro un blocco preciso.
   *  Il filtro include il blocco: lo stesso esercizio può comparire in blocchi
   *  diversi della stessa sessione, e contandolo per solo nome i conteggi dei
   *  due blocchi si sommavano. */
  function serieDi(bloccoNum, nomeEsercizio) {
    return serieSalvate.filter(s => s.blocco_numero === bloccoNum && s.esercizio_nome === nomeEsercizio)
  }

  /** Quante volte il coach ha previsto questo esercizio, se l'ha previsto.
   *  Leggeva solo ex.serie, che negli esercizi a tempo è sempre vuoto: il tetto
   *  non scattava mai e si potevano registrare serie all'infinito. Ora nei
   *  formati a struttura il numero lo danno i giri del blocco. */
  const seriePreviste = ripetizioniPreviste

  /** Secondi di lavoro di una serie a tempo: la regola sta in lib/formati, la
   *  stessa che legge il coach mentre crea il blocco. Teneva due copie della
   *  logica — una qui e una in CreaSessione — e divergevano: la scheda mostrava
   *  la durata dell'esercizio (30s) mentre il cronometro usava quella del
   *  blocco (40s), due numeri diversi per la stessa cosa. */
  const secondiSerie = secondiEsercizio

  async function salvaSerie(bloccoNum, bloccoNome, esercizio, blocco, durataForzata = null) {
    if (!risultatoAperto) { toast_msg('Inizia prima la sessione'); return }
    if (salvandoSerie) return   // un salvataggio alla volta: vedi salvandoSerie
    const logKey = `${bloccoNum}_${esercizio.nome}`
    const log = logData[logKey] || {}
    const previste = seriePreviste(blocco, esercizio)
    const fatte = serieDi(bloccoNum, esercizio.nome).length

    // Tetto alle serie: quelle previste dal coach sono il piano, non un consiglio.
    if (previste && fatte >= previste) {
      toast_msg(`Hai già registrato tutte e ${previste} le serie previste`)
      return
    }

    const durata = esercizio.tipo_misura === 'tempo'
      ? (durataForzata ?? (log.durata || esercizio.durata || null))
      : null
    // ?? e non ||, come per il carico: il campo è controllato su questa stessa
    // espressione, quindi un valore scritto a mano vince sempre su quello
    // previsto dal coach, anche se è zero.
    const ripetizioni = esercizio.tipo_misura !== 'tempo' ? (log.rip ?? esercizio.ripetizioni ?? null) : null

    // Il carico lo calcola caricoDi, la STESSA funzione che riempie il campo a
    // schermo (vedi lib/formati.js). È l'unico modo per essere certi che il
    // numero salvato sia quello che l'atleta ha davanti agli occhi: finché
    // campo e salvataggio chiamano la stessa funzione, non possono divergere.
    const ultimo = esercizio.esercizioId ? ultimiCarichi[esercizio.esercizioId] : null
    const { peso, elastico } = caricoDi(log, ultimo)

    setSalvandoSerie(logKey)
    try {
      // Esito controllato su ENTRAMBE le insert: prima nessuna delle due lo era,
      // e l'atleta leggeva "✅ serie 2 di 3" anche quando non era stato salvato
      // niente. È l'azione centrale dell'allenamento: deve dire la verità.
      const { error: errSerie } = await supabase.from('risultati_serie').insert({
        risultato_sessione_id: risultatoAperto.id,
        blocco_numero: bloccoNum,
        blocco_nome: bloccoNome,
        esercizio_nome: esercizio.nome,
        esercizio_id: esercizio.esercizioId || null,
        variante_nome: esercizio.varianteNome || null,
        // Numero vero della serie. Prima era "log.serie || 1" e log.serie non
        // veniva mai impostato: tutte le serie finivano salvate come numero 1.
        serie_numero: fatte + 1,
        ripetizioni,
        durata_secondi: durata,
        peso,
        elastico,
        note: log.note || null,
      })
      if (errSerie) { toast_msg('Serie NON salvata: ' + errSerie.message); return }

      let trackingOk = true
      if (esercizio.esercizioId) {
        const { error: errTracking } = await supabase.from('tracking_esercizio').insert({
          atleta_id: profilo.id, esercizio_id: esercizio.esercizioId,
          data: new Date().toISOString().split('T')[0],
          variante_nome: esercizio.varianteNome || log.variante || null,
          // Il numero della serie finiva solo in risultati_serie: nei progressi
          // le righe erano indistinguibili e si potevano ordinare solo per orario.
          serie_numero: fatte + 1,
          ripetizioni,
          durata_secondi: durata,
          peso,
          elastico,
        })
        if (errTracking) { console.error('[atleta] tracking_esercizio:', errTracking); trackingOk = false }
      }

      const oraFatte = fatte + 1
      // La serie è salvata, ma i progressi no: va detto, perché è il grafico
      // dell'atleta a restare incompleto e nient'altro lo segnalerebbe. Il
      // messaggio è uno solo: due toast di fila si sovrascrivono a vicenda e
      // quello importante sparisce dopo due millesimi.
      toast_msg(!trackingOk
        ? `⚠️ ${esercizio.nome}: serie salvata, ma non è finita nei progressi`
        : previste
          ? `✅ ${esercizio.nome} · serie ${oraFatte} di ${previste}`
          : `✅ ${esercizio.nome} salvato!`)
      // Il carico resta, il resto si azzera. Fra una serie e l'altra il peso è
      // quasi sempre lo stesso, e ribatterlo ogni volta è la scomodità che
      // portava a non toccare il campo — cioè la causa del difetto di prima.
      // Le ripetizioni invece tornano a quelle previste dal coach, che sono il
      // punto di partenza giusto per la serie successiva.
      setLogData(prev => {
        const precedente = prev[logKey] || {}
        return { ...prev, [logKey]: {
          usaElastico: precedente.usaElastico,
          kg: precedente.kg,
          elastico: precedente.elastico,
        } }
      })
      // Senza un numero di serie previsto resta il vecchio comportamento: un
      // salvataggio chiude l'esercizio, e si riapre con "+ Aggiungi serie".
      // Con un numero previsto è il conteggio a decidere quando è finito.
      if (!previste) setExCompletati(prev => new Set([...prev, logKey]))
      // Formato Classico: parte il countdown di recupero impostato dal coach per quel blocco.
      // Non dopo l'ultima serie: lì non c'è più niente da recuperare.
      if (blocco?.formato === 'classico' && (!previste || oraFatte < previste)) {
        const riposo = blocco.impostazioni?.riposo || 90
        setRecuperoAttivo({ logKey, secondi: riposo })
      }
      if (risultatoAperto?.id) await caricaSerieSalvate(risultatoAperto.id)
    } finally {
      setSalvandoSerie(null)
    }
  }

  async function caricaSerieSalvate(risultatoId) {
    const { data } = await supabase.from('risultati_serie').select('*').eq('risultato_sessione_id', risultatoId).order('creato_il', { ascending: true })
    setSerieSalvate(data || [])
  }

  async function aggiornaSerie(serieId) {
    const d = serieEditData[serieId] || {}
    const { error } = await supabase.from('risultati_serie').update({
      ripetizioni: d.ripetizioni ?? null, durata_secondi: d.durata_secondi ?? null,
      peso: d.usaElastico ? 0 : (d.peso ?? 0), elastico: d.usaElastico ? (d.elastico ?? null) : null,
    }).eq('id', serieId)
    if (error) { toast_msg('Serie NON aggiornata: ' + error.message); return }
    toast_msg('✅ Serie aggiornata!')
    setSerieInModifica(null)
    if (risultatoAperto?.id) caricaSerieSalvate(risultatoAperto.id)
  }

  async function eliminaSerie(serieId) {
    const { error } = await supabase.from('risultati_serie').delete().eq('id', serieId)
    if (error) { toast_msg('Serie NON eliminata: ' + error.message); return }
    toast_msg('Serie eliminata')
    if (risultatoAperto?.id) caricaSerieSalvate(risultatoAperto.id)
  }

  async function caricaSessioneSpecifica(sessioneCalId) {
    const { data: calSess } = await supabase.from('sessioni_calendario').select('*, sessioni_tipo(*)').eq('id', sessioneCalId).maybeSingle()
    if (!calSess) return
    const blocchiRaw = calSess.sessioni_tipo?.blocchi || []
    const exIds = blocchiRaw.flatMap(b => b.esercizi || []).map(e => e.esercizioId).filter(Boolean)
    let variantiMap = {}
    if (exIds.length > 0) {
      const { data: esDb } = await supabase.from('esercizi_db').select('id, varianti, regressioni').in('id', exIds)
      if (esDb) esDb.forEach(e => { variantiMap[e.id] = [...comeLista(e.varianti), ...comeLista(e.regressioni)] })
    }
    const blocchiAggiornati = blocchiRaw.map(b => ({ ...b, esercizi: (b.esercizi || []).map(ex => ({ ...ex, varianti: variantiMap[ex.esercizioId] || ex.varianti || [] })) }))
    setSessioneOggi({ ...calSess, sessioni_tipo: { ...calSess.sessioni_tipo, blocchi: blocchiAggiornati } })
    setRisultatoAperto(null)
    setSerieSalvate([])
    setLogData({})
    setExCompletati(new Set())
    const { data: ris } = await supabase.from('risultati_sessione').select('*').eq('sessione_calendario_id', calSess.id).eq('atleta_id', profilo.id).maybeSingle()
    if (ris) { setRisultatoAperto(ris); caricaSerieSalvate(ris.id) }
    caricaUltimiCarichi(exIds)
    setVistaSessioneGruppo(true)
    setVistaGruppi(false)
    setMostraSessPersonale(false)
  }

  // Apre la vista dettaglio di una sessione di gruppo completata — usata sia da home che da storico gruppo
  async function apriSessioneDettaglio(sessione, risultatoId) {
    setSessioneDettaglio({ ...sessione, risultatoId })
    if (risultatoId) await caricaSerieStorico(risultatoId)
  }

  // ── CARICA SERIE DI UNA SESSIONE STORICO GRUPPI ──
  async function caricaSerieStorico(risultatoId) {
    setStorGruppoSerie([])
    if (!risultatoId) return
    const { data } = await supabase.from('risultati_serie')
      .select('*').eq('risultato_sessione_id', risultatoId)
      .order('creato_il', { ascending: true })
    setStorGruppoSerie(data || [])
  }

  async function aggiornaSerieStorico(serieId) {
    const d = storGruppoEdit[serieId] || {}
    const { error } = await supabase.from('risultati_serie').update({
      ripetizioni: d.ripetizioni ?? null,
      durata_secondi: d.durata_secondi ?? null,
      peso: d.usaElastico ? 0 : (d.peso ?? 0),
      elastico: d.usaElastico ? (d.elastico ?? null) : null,
    }).eq('id', serieId)
    if (error) { toast_msg('Risultato NON aggiornato: ' + error.message); return }
    toast_msg('✅ Risultato aggiornato!')
    setStorGruppoMod(null)
    // Ricarica
    const serie = storGruppoSerie.find(s => s.id === serieId)
    if (serie) caricaSerieStorico(serie.risultato_sessione_id)
  }

  function setStorEdit(serieId, campo, val) {
    setStorGruppoEdit(prev => ({ ...prev, [serieId]: { ...(prev[serieId] || {}), [campo]: val } }))
  }

  function setLog(key, campo, val) { setLogData(p => ({ ...p, [key]: { ...(p[key] || {}), [campo]: val } })) }
  function toast_msg(msg) { setToast(msg); setTimeout(() => setToast(''), 2500) }

  const oggi = new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })

  if (mostraNotifiche) return <PaginaNotifiche
    onChiudi={() => { setMostraNotifiche(false); caricaDati(); window.__topBarAggiorna?.() }}
    onNavigate={(dest, richiestaId, datiExtra) => {
      setMostraNotifiche(false)
      if (dest === 'gruppo_richieste') {
        // Richiesta di ingresso: apre direttamente il gruppo sul tab Membri,
        // dove stanno Approva e Rifiuta. Riusa il passaggio già impiegato per
        // aprire un gruppo specifico da un'altra schermata.
        if (datiExtra?.gruppo_id) {
          sessionStorage.setItem('fp_apri_gruppo', JSON.stringify({
            id: datiExtra.gruppo_id, nome: datiExtra.gruppo_nome, tab: 'membri',
          }))
        }
        if (onCambiaTab) onCambiaTab('gruppi')
      } else if (dest === 'richieste') {
        // FIX: usa evento custom invece di window flag per evitare race condition
        window.__apriRichieste = richiestaId || true
        if (onCambiaTab) onCambiaTab('profilo')
      } else if (dest === 'home') {
        // Notifica sessione → tab oggi (home)
        setTab('oggi')
        // Torna alla home resettando le viste secondarie
        setVistaGruppi(false)
        setVistaProgressi(false)
        setMostraSessPersonale(false)
        setVistaSessioneGruppo(false)
        setSessioneDettaglio(null)
      } else if (dest === 'messaggi') {
        setTab('messaggi')
        setVistaGruppi(false)
        setVistaProgressi(false)
        setMostraSessPersonale(false)
        setVistaSessioneGruppo(false)
        // Ricarica messaggi — stessa logica del caricamento iniziale
        ;(async () => {
          const { data: memb } = await supabase
            .from('gruppo_membri').select('gruppo_id')
            .eq('utente_id', profilo.id).eq('stato', 'attivo')
          const ids = (memb || []).map(m => m.gruppo_id)
          let msgs = []
          if (ids.length > 0) {
            const { data } = await supabase.from('messaggi')
              .select('*, profili!messaggi_mittente_id_fkey(nome,cognome)')
              .or(`destinatario_id.eq.${profilo.id},gruppo_id.in.(${ids.join(',')})`)
              .order('creato_il', { ascending: false }).limit(50)
            msgs = data || []
          } else {
            const { data } = await supabase.from('messaggi')
              .select('*, profili!messaggi_mittente_id_fkey(nome,cognome)')
              .eq('destinatario_id', profilo.id)
              .order('creato_il', { ascending: false }).limit(50)
            msgs = data || []
          }
          setMessaggi(msgs)
        })()
      } else if (dest === 'adesioni') {
        if (onCambiaTab) onCambiaTab('adesioni')
      } else {
        setTab(dest)
      }
    }}
  />

  // Vista lista gruppi atleta
  if (vistaGruppi) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }} className="fade-in">
      <TopBar
        onIndietro={() => { if (gruppoDettaglioSel) { setGruppoDettaglioSel(null) } else { setVistaGruppi(false); if (onCambiaTab) onCambiaTab('home') } }}
        onNotifiche={() => setMostraNotifiche(true)}
      />

      {/* Sub-nav del gruppo selezionato */}
      {gruppoDettaglioSel && (
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 16px' }}>
          {[{ id: 'prossime', label: 'Prossime sessioni' }, { id: 'storico', label: 'Storico' }].map(t => (
            <button key={t.id} onClick={() => setTabGruppo(t.id)} style={{ padding: '9px 14px', background: 'none', border: 'none', borderBottom: `2px solid ${tabGruppo === t.id ? 'var(--accent)' : 'transparent'}`, color: tabGruppo === t.id ? 'var(--accent)' : 'var(--muted)', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'Syne' }}>{t.label}</button>
          ))}
        </div>
      )}

      <div className="scroll-area">
        {/* Titolo sezione */}
        {!gruppoDettaglioSel && (
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 20, marginBottom: 4 }}>
            Gruppi · atleta
          </div>
        )}
        {gruppoDettaglioSel && (
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 20, marginBottom: 4 }}>
            {gruppoDettaglioSel.nome}
          </div>
        )}
        {!gruppoDettaglioSel && invitiPending.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 14, marginBottom: 8, color: 'var(--accent2)' }}>
              ⏳ Inviti in attesa ({invitiPending.length})
            </div>
            {invitiPending.map(m => (
              <div key={m.id} className="card" style={{ border: '1px solid rgba(87,184,255,0.3)', marginBottom: 8 }}>
                <div className="card-body" style={{ padding: '12px 14px' }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 14 }}>{m.gruppi?.nome}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2, marginBottom: 10 }}>
                    {m.gruppi?.profili?.nome ? `Coach: ${m.gruppi.profili.nome} ${m.gruppi.profili.cognome || ''}` : 'Coach non più attivo'}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary" style={{ flex: 1, padding: '8px', fontSize: 13 }}
                      onClick={() => rispondiInvitoGruppo(m.id, m.gruppo_id, 'accetta')}>
                      ✓ Accetta
                    </button>
                    <button className="btn btn-ghost" style={{ flex: 1, padding: '8px', fontSize: 13, color: 'var(--red)', borderColor: 'rgba(248,113,113,0.4)' }}
                      onClick={() => rispondiInvitoGruppo(m.id, m.gruppo_id, 'rifiuta')}>
                      ✕ Rifiuta
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Lista gruppi */}
        {!gruppoDettaglioSel && (gruppiDettaglio.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>👥</div>
            <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Nessun gruppo</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>Non sei ancora iscritto a nessun gruppo. Il tuo coach ti inviterà via email.</div>
          </div>
        ) : gruppiDettaglio.map(g => (
          <div key={g.id} className="card" onClick={() => { caricaDettaglioGruppo(g); setTabGruppo('prossime') }} style={{ cursor: 'pointer' }}>
            <div className="card-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 16, marginBottom: 8 }}>{g.nome}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    {/* Senza coach l'avatar resterebbe un cerchio vuoto */}
                    {g.profili?.nome && (
                      <div className="avatar" style={{ width: 26, height: 26, fontSize: 10, background: 'rgba(127,214,61,0.15)', color: 'var(--accent)' }}>
                        {g.profili.nome[0]}{g.profili.cognome?.[0] || ''}
                      </div>
                    )}
                    {/* Il coach può aver cancellato l'account: il gruppo resta per lo storico degli atleti */}
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>{g.profili?.nome ? `Coach: ${g.profili.nome} ${g.profili.cognome || ''}` : 'Coach non più attivo'}</span>
                  </div>
                  {prossimeSessioniTutti.filter(s => s.gruppo_id === g.id).slice(0, 1).map(s => {
                    const d = new Date(s.data + 'T00:00:00')
                    const diff = Math.round((d - new Date().setHours(0,0,0,0)) / 86400000)
                    return (
                      <div key={s.id} style={{ fontSize: 12, color: 'var(--muted)' }}>
                        Prossima: <strong style={{ color: 'var(--text)' }}>{s.sessioni_tipo?.nome}</strong>
                        {' — '}{diff === 0 ? 'Oggi' : diff === 1 ? 'Domani' : d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
                      </div>
                    )
                  })}
                  {prossimeSessioniTutti.filter(s => s.gruppo_id === g.id).length === 0 && (
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>Nessuna sessione in programma</div>
                  )}
                </div>
                <span style={{ color: 'var(--muted)', fontSize: 18 }}>›</span>
              </div>
            </div>
          </div>
        )))}

        {/* Dettaglio gruppo — Prossime sessioni */}
        {gruppoDettaglioSel && tabGruppo === 'prossime' && (caricamentoGruppo ? (
          <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>Caricamento...</div>
        ) : sessioniGruppo.prossime.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📅</div>
            <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Nessuna sessione in programma</div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>Il coach non ha ancora programmato nuove sessioni</div>
          </div>
        ) : sessioniGruppo.prossime.map(s => {
          const d = new Date(s.data + 'T00:00:00')
          const oggi2 = new Date(); oggi2.setHours(0,0,0,0)
          const diff = Math.round((d - oggi2) / 86400000)
          const isOggi = diff === 0
          const etichetta = diff === 0 ? 'Oggi' : diff === 1 ? 'Domani' : `tra ${diff} giorni`
          const giaCompletata = s.mioRisultato?.completata
          return (
            <div key={s.id} className="card">
              <div className="card-header">
                <div>
                  <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 15 }}>{s.sessioni_tipo?.nome || 'Sessione'}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                    {d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                    {s.sessioni_tipo?.durata_minuti && ` · ${s.sessioni_tipo.durata_minuti} min`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {giaCompletata ? (
                    <span className="badge badge-green">✓ Fatta</span>
                  ) : (
                    <>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: isOggi ? 'rgba(127,214,61,0.2)' : 'var(--surface2)', color: isOggi ? 'var(--accent)' : 'var(--muted)' }}>{etichetta}</span>
                      {isOggi && <button onClick={() => { caricaSessioneSpecifica(s.id); setVistaGruppi(false) }} className="btn btn-primary btn-sm">Inizia →</button>}
                    </>
                  )}
                </div>
              </div>
              {(s.sessioni_tipo?.blocchi || []).length > 0 && (
                <div style={{ padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {/* Anteprima: i blocchi qui sono dentro la card della sessione,
                      quindi non prendono il trattamento pieno — basta la costa
                      colorata e il formato nel suo colore per distinguerli. */}
                  {(s.sessioni_tipo.blocchi || []).map((b, bi) => (
                    <div key={bi} style={{ background: 'var(--surface2)', borderRadius: 10, padding: '10px 12px', border: '1px solid var(--border)', borderLeft: `4px solid ${coloreFormato(b.formato).testo}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                        <span style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 13 }}>{b.nome?.trim() || nomeFormato(b.formato)}</span>
                        {b.nome?.trim() && <span style={{ fontSize: 11, color: coloreFormato(b.formato).testo, marginLeft: 'auto' }}>{nomeFormato(b.formato)}</span>}
                      </div>
                      {(b.esercizi || []).map((ex, ei) => (
                        <div key={ei} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 13 }}>
                          <span style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 11, minWidth: 16 }}>{ei + 1}.</span>
                          <span style={{ flex: 1 }}>{ex.nomeEsercizio || ex.nome || '?'}</span>
                          <span style={{ color: 'var(--muted)', fontSize: 11 }}>
                            {ex.tipo_misura === 'tempo' ? (ex.durata ? `${ex.durata}s` : '') : ex.serie && ex.ripetizioni ? `${ex.serie}×${ex.ripetizioni}` : ex.ripetizioni ? `${ex.ripetizioni} rip` : ex.durata ? `${ex.durata}s` : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        }))}

        {/* Dettaglio gruppo — Storico */}
        {gruppoDettaglioSel && tabGruppo === 'storico' && (caricamentoGruppo ? (
          <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>Caricamento...</div>
        ) : sessioniGruppo.storico.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🏁</div>
            <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 15 }}>Nessuna sessione svolta</div>
          </div>
        ) : sessioniGruppo.storico.map(s => {
          const ris = s.mioRisultato
          return (
            <div key={s.id} className="card" style={{ border: '1px solid rgba(87,184,255,0.15)', cursor: 'pointer' }}
              onClick={() => apriSessioneDettaglio(s, ris?.id)}>
              <div className="card-header" style={{ background: 'rgba(87,184,255,0.03)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 14 }}>👤</span>
                    <div className="card-title" style={{ fontSize: 13 }}>{s.sessioni_tipo?.nome || 'Sessione'}</div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                    {new Date(s.data + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>
                <span className={`badge ${ris ? (ris.completata ? 'badge-green' : 'badge-orange') : ''}`}
                  style={{ fontSize: 10, ...(!ris ? { background: 'var(--surface2)', color: 'var(--muted)' } : {}) }}>
                  {ris ? (ris.completata ? '✓ Fatta' : 'Parziale') : 'Non svolta'}
                </span>
              </div>
              {/* Anteprima esercizi */}
              <div style={{ padding: '6px 16px 10px', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {(s.sessioni_tipo?.blocchi || []).flatMap(b => b.esercizi || []).slice(0, 5).map((ex, j) => (
                  <span key={j} style={{ fontSize: 10, padding: '2px 8px', background: 'rgba(87,184,255,0.08)', color: 'var(--accent2)', borderRadius: 10, border: '1px solid rgba(87,184,255,0.2)' }}>
                    {ex.nomeEsercizio || ex.nome}
                  </span>
                ))}
              </div>
            </div>
          )
        }))}
      </div>
    </div>

  )

  // Vista dettaglio gruppo
  if (gruppoDettaglioSel) {
    const { storico, prossime } = sessioniGruppo

    function eserciziBlocchi(blocchi) {
      if (!blocchi) return []
      return blocchi.flatMap(b => (b.esercizi || []).map(e => ({ nome: e.nomeEsercizio || e.nome || '?', formato: b.formato })))
    }

    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }} className="fade-in">
        <TopBar
          onIndietro={() => setGruppoDettaglioSel(null)}
          onNotifiche={() => setMostraNotifiche(true)}
        />

        <div className="scroll-area">
          {caricamentoGruppo ? (
            <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>Caricamento...</div>
          ) : <>

            <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 20, marginBottom: 4 }}>
              {gruppoDettaglioSel.nome}
            </div>

            {/* Prossime sessioni */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">Prossime sessioni</div>
                <span className="badge badge-accent">{prossime.length}</span>
              </div>
              <div style={{ padding: '0 16px' }}>
                {prossime.length === 0 ? (
                  <div style={{ padding: '16px 0', textAlign: 'center', fontSize: 13, color: 'var(--muted)' }}>Nessuna sessione in programma</div>
                ) : prossime.map((s, i) => {
                  const esercizi = eserciziBlocchi(s.sessioni_tipo?.blocchi)
                  const isOggi = s.data === new Date().toISOString().split('T')[0]
                  return (
                    <div key={s.id} style={{ padding: '12px 0', borderBottom: i < prossime.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{s.sessioni_tipo?.nome || 'Sessione'}</div>
                          <div style={{ fontSize: 12, color: isOggi ? 'var(--accent)' : 'var(--muted)', marginTop: 2 }}>
                            {isOggi ? '⚡ Oggi' : new Date(s.data + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0, marginLeft: 8 }}>
                          <span className="badge badge-blue">{s.sessioni_tipo?.durata_minuti || '?'} min</span>
                          {isOggi && (
                            <button className="btn btn-primary btn-sm" onClick={() => {
                              caricaSessioneSpecifica(s.id)
                              setGruppoDettaglioSel(null)
                              setVistaGruppi(false)
                              if (onCambiaTab) onCambiaTab('home')
                            }}>▶ Inizia</button>
                          )}
                        </div>
                      </div>
                      {esercizi.length > 0 && (
                        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {esercizi.map((e, j) => (
                            <span key={j} style={{ fontSize: 10, background: 'var(--surface2)', color: 'var(--muted)', padding: '2px 8px', borderRadius: 10, border: '1px solid var(--border)' }}>
                              {e.nome}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Storico sessioni */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">Storico sessioni</div>
                <span className="badge">{storico.length}</span>
              </div>
              <div style={{ padding: '0 16px' }}>
                {storico.length === 0 ? (
                  <div style={{ padding: '16px 0', textAlign: 'center', fontSize: 13, color: 'var(--muted)' }}>Nessuna sessione completata</div>
                ) : storico.map((s, i) => {
                  const esercizi = eserciziBlocchi(s.sessioni_tipo?.blocchi)
                  return (
                    <div key={s.id} style={{ padding: '12px 0', borderBottom: i < storico.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{s.sessioni_tipo?.nome || 'Sessione'}</div>
                          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                            {new Date(s.data + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>{s.sessioni_tipo?.durata_minuti || '?'} min</span>
                      </div>
                      {esercizi.length > 0 && (
                        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {esercizi.map((e, j) => (
                            <span key={j} style={{ fontSize: 10, background: 'var(--surface2)', color: 'var(--muted)', padding: '2px 8px', borderRadius: 10, border: '1px solid var(--border)' }}>
                              {e.nome}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </>}
        </div>
      </div>
    )
  }

  // ── VISTA SESSIONE PERSONALE ──
  // Tutto il flusso "allenati da solo" vive in SessionePersonale.js. Viene montato
  // da zero a ogni apertura (key sulla sequenza), così il suo stato interno
  // riparte pulito senza bisogno di reset manuali qui.
  if (mostraSessPersonale) return (
    <SessionePersonale
      key={richiestaSessPersonaleSeq}
      profilo={profilo}
      richiesta={richiestaSessPersonale}
      sessPersonali={sessPersonali}
      setSessPersonali={setSessPersonali}
      eserciziFavoriti={eserciziFavoriti}
      tracking={tracking}
      ultimiCarichi={ultimiCarichi}
      caricaUltimiCarichi={caricaUltimiCarichi}
      onChiudi={() => setMostraSessPersonale(false)}
      onCambiaTab={onCambiaTab}
      onEsecuzioneAttiva={setSessPersonaleEsecuzioneAttiva}
      onDatiCambiati={caricaDati}
      onNotifiche={() => setMostraNotifiche(true)}
      conferma={conferma}
      setConferma={setConferma}
      toast={toast}
      toastMsg={toast_msg}
    />
  )

  // ── VISTA DETTAGLIO SESSIONE GRUPPO (completata) ──
  // Aperta sia da home (sessione fatta) sia da storico gruppo
  if (sessioneDettaglio) {
    const s = sessioneDettaglio
    const ris = s.mioRisultato
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }} className="fade-in">
        <TopBar
          onIndietro={() => { setSessioneDettaglio(null); setStorGruppoSerie([]) }}
          onNotifiche={() => setMostraNotifiche(true)}
        />
        <div className="scroll-area">
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 20 }}>{s.sessioni_tipo?.nome || 'Sessione'}</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
              {s.nomeGruppo ? `${s.nomeGruppo} · ` : ''}
              {new Date(s.data + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
            <div style={{ marginTop: 8 }}>
              <span className={`badge ${ris?.completata ? 'badge-green' : 'badge-orange'}`}>
                {ris?.completata ? '✓ Completata' : ris ? 'Parziale' : 'Non svolta'}
              </span>
            </div>
          </div>

          {(s.sessioni_tipo?.blocchi || []).map((blocco, bi) => (
            <div key={bi} className="card" style={stileCardBlocco(blocco.formato)}>
              <IntestazioneBlocco numero={bi + 1} blocco={blocco} />
              <div style={{ padding: '0 16px' }}>
                {(blocco.esercizi || []).map((ex, ei) => {
                  // blocco_numero, non blocco_num: quella colonna non esiste in
                  // risultati_serie, quindi il confronto era sempre falso e a
                  // distinguere i blocchi restava solo il nome — che è opzionale.
                  // Con due blocchi senza nome le serie dell'uno comparivano
                  // anche sotto l'altro.
                  const serieEx = storGruppoSerie.filter(s2 =>
                    s2.esercizio_nome === (ex.nomeEsercizio || ex.nome) &&
                    s2.blocco_numero === bi + 1
                  )
                  return (
                    <div key={ei} style={{ padding: '12px 0', borderBottom: ei < (blocco.esercizi || []).length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div>
                          <span style={{ fontWeight: 600, fontSize: 14 }}>{ex.nomeEsercizio || ex.nome}</span>
                          <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 8 }}>
                            {ex.tipo_misura === 'tempo' ? (ex.durata ? `${ex.durata}s` : '') : ex.serie && ex.ripetizioni ? `${ex.serie}×${ex.ripetizioni}` : ex.ripetizioni ? `${ex.ripetizioni} rip` : ex.durata ? `${ex.durata}s` : ''}
                          </span>
                        </div>
                      </div>
                      {ris ? (serieEx.length > 0 ? serieEx.map((serie, si) => {
                        const inMod = storGruppoMod === serie.id
                        const edit = storGruppoEdit[serie.id] || {}
                        return (
                          <div key={serie.id} style={{ background: 'var(--surface2)', borderRadius: 10, padding: '8px 10px', marginBottom: 6, border: '1px solid var(--border)' }}>
                            {!inMod ? (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontSize: 13 }}>
                                  <span style={{ color: 'var(--muted)' }}>Serie {serie.serie_numero ?? si + 1}:</span>{' '}
                                  {serie.durata_secondi ? `${serie.durata_secondi}s` : `${serie.ripetizioni || '?'} rip`}
                                  {serie.elastico ? ` · 🦺 ${serie.elastico}` : serie.peso > 0 ? ` · ${serie.peso}kg` : ''}
                                </div>
                                <button onClick={() => {
                                  setStorGruppoMod(serie.id)
                                  setStorGruppoEdit(p => ({ ...p, [serie.id]: {
                                    ripetizioni: serie.ripetizioni, durata_secondi: serie.durata_secondi,
                                    peso: serie.peso, elastico: serie.elastico,
                                    usaElastico: !!serie.elastico
                                  }}))
                                }} title="Modifica risultato" style={{ background: 'rgba(127,214,61,0.1)', border: '1px solid rgba(127,214,61,0.35)', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 14 }}>✏️</button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                                  {serie.durata_secondi !== null ? (
                                    <div style={{ flex: 1 }}><input className="num-input" type="number"
                                      value={edit.durata_secondi ?? serie.durata_secondi}
                                      onChange={e => setStorEdit(serie.id, 'durata_secondi', +e.target.value)} style={{ fontSize: 15 }} />
                                      <div style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center', marginTop: 2 }}>SEC</div></div>
                                  ) : (
                                    <div style={{ flex: 1 }}><input className="num-input" type="number"
                                      value={edit.ripetizioni ?? serie.ripetizioni ?? ''}
                                      onChange={e => setStorEdit(serie.id, 'ripetizioni', +e.target.value)} style={{ fontSize: 15 }} />
                                      <div style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center', marginTop: 2 }}>RIP</div></div>
                                  )}
                                  {!edit.usaElastico ? (
                                    <div style={{ flex: 1 }}><input className="num-input" type="number"
                                      value={edit.peso ?? serie.peso ?? 0}
                                      onChange={e => setStorEdit(serie.id, 'peso', +e.target.value)} style={{ fontSize: 15 }} />
                                      <div style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center', marginTop: 2 }}>KG</div></div>
                                  ) : (
                                    <div style={{ flex: 2 }}><select className="form-select" style={{ fontSize: 12, padding: '6px' }}
                                      value={edit.elastico ?? serie.elastico ?? ''}
                                      onChange={e => setStorEdit(serie.id, 'elastico', e.target.value)}>
                                      <option value="">-- colore --</option>
                                      {['Giallo (leggero)','Verde','Rosso','Blu','Viola','Nero (forte)'].map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                      <div style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center', marginTop: 2 }}>ELASTICO</div></div>
                                  )}
                                </div>
                                {ex.consenteElastico && (
                                  <button onClick={() => setStorGruppoEdit(p => ({ ...p, [serie.id]: { ...p[serie.id], usaElastico: !edit.usaElastico } }))}
                                    style={{ background: 'none', border: `1px solid ${edit.usaElastico ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 8, color: edit.usaElastico ? 'var(--accent)' : 'var(--muted)', fontSize: 11, padding: '3px 10px', cursor: 'pointer', alignSelf: 'flex-start' }}>
                                    🦺 {edit.usaElastico ? 'Usa kg' : 'Usa elastico'}
                                  </button>
                                )}
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => aggiornaSerieStorico(serie.id)}>Salva</button>
                                  <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => setStorGruppoMod(null)}>Annulla</button>
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      }) : (
                        <div style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic', padding: '4px 0' }}>
                          Nessun risultato registrato per questo esercizio
                        </div>
                      )) : (
                        <div style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic', padding: '4px 0' }}>
                          Sessione non svolta
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
        {conferma && <ModaleConferma {...conferma} onAnnulla={() => setConferma(null)} />}
        {toast && <div className="toast">{toast}</div>}
      </div>
    )
  }

  // ── VISTA ESECUZIONE SESSIONE DI GRUPPO ──
  if (vistaSessioneGruppo && sessioneOggi) {
    const blocchi = sessioneOggi?.sessioni_tipo?.blocchi || []
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }} className="fade-in">
        {timerBlocco && <TimerBlocco blocco={timerBlocco} onChiudi={() => setTimerBlocco(null)} />}
        {timerSerie && (
          <TimerSerie
            secondi={timerSerie.secondi}
            nomeEsercizio={timerSerie.esercizio.nomeEsercizio || timerSerie.esercizio.nome}
            serieNumero={timerSerie.serieNumero}
            seriePreviste={timerSerie.seriePreviste}
            onAnnulla={() => setTimerSerie(null)}
            onCompletata={async (durataEseguita) => {
              const s = timerSerie
              await salvaSerie(s.bloccoNum, s.bloccoNome, s.esercizio, s.blocco, durataEseguita)
              // Si chiude da sé dopo la conferma: a fine serie si è senza fiato,
              // e cercare un pulsante è l'ultima cosa che si vuole fare.
              setTimeout(() => setTimerSerie(null), 1600)
            }}
          />
        )}
        {conferma && <ModaleConferma {...conferma} onAnnulla={() => setConferma(null)} />}
        <TopBar
          onIndietro={indietroSessioneGruppo}
          onNotifiche={() => setMostraNotifiche(true)}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px 0' }}>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 18 }}>
            {sessioneOggi.sessioni_tipo?.nome || 'Sessione'}
          </div>
          {!risultatoAperto
            ? <button className="btn btn-primary btn-sm" onClick={iniziaSessione}>▶ Inizia</button>
            : risultatoAperto.completata
              ? <span className="badge badge-green">✓ Completata</span>
              : null
          }
        </div>

        <div style={{ padding: '4px 20px 8px', fontSize: 11, color: 'var(--muted)' }}>
          {sessioneOggi.sessioni_tipo?.durata_minuti} min · {blocchi.length} blocchi
        </div>

        {risultatoAperto && !risultatoAperto.completata && (
          <div style={{ margin: '0 16px 8px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="alert alert-success">
              <span>⚡</span>
              <div><strong style={{ display: 'block' }}>Sessione in corso</strong>Registra i risultati per ogni esercizio.</div>
            </div>
            <button
              onClick={completaSessione}
              style={{
                padding: '8px 20px', borderRadius: 20, border: 'none',
                background: 'var(--accent)', color: '#000',
                fontFamily: 'Syne', fontWeight: 700, fontSize: 13,
                cursor: 'pointer', display: 'block', margin: '0 auto',
              }}>
              ✓ Completa sessione
            </button>
          </div>
        )}
        {risultatoAperto?.completata && (
          <div className="alert alert-info" style={{ margin: '0 16px 8px', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span>✅</span><strong>Sessione completata!</strong></div>
          </div>
        )}

        <div className="scroll-area">

          {/* Serie già registrate */}
          {risultatoAperto && serieSalvate.length > 0 && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">📋 Serie registrate</div>
                <span className="badge badge-accent">{serieSalvate.length}</span>
              </div>
              <div style={{ padding: '0 16px' }}>
                {serieSalvate.map((s, i) => {
                  const inMod = serieInModifica === s.id
                  const edit = serieEditData[s.id] || {}
                  return (
                    <div key={s.id} style={{ padding: '10px 0', borderBottom: i < serieSalvate.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      {!inMod ? (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{s.esercizio_nome}</div>
                            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                              {/* Il numero vero della serie, non la posizione nella
                                  lista: con più esercizi le due cose non coincidono. */}
                              <span style={{ marginRight: 6 }}>Serie {s.serie_numero ?? i + 1}{s.blocco_nome ? ` · ${s.blocco_nome}` : ''} ·</span>
                              {s.durata_secondi ? `${s.durata_secondi}s` : `${s.ripetizioni || '?'} rip`}
                              {s.elastico ? ` · 🦺 ${s.elastico}` : s.peso > 0 ? ` · ${s.peso}kg` : ''}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => { setSerieInModifica(s.id); setSerieEditData(p => ({ ...p, [s.id]: { ripetizioni: s.ripetizioni, durata_secondi: s.durata_secondi, peso: s.peso, elastico: s.elastico, usaElastico: !!s.elastico } })) }}
                              title="Modifica serie" style={{ background: 'rgba(127,214,61,0.1)', border: '1px solid rgba(127,214,61,0.35)', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 14 }}>✏️</button>
                            <button onClick={() => eliminaSerie(s.id)}
                              title="Elimina serie" style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.35)', borderRadius: 8, padding: '4px 8px', cursor: 'pointer', fontSize: 14 }}>🗑️</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{s.esercizio_nome}</div>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            {s.durata_secondi !== null ? (
                              <div style={{ flex: 1 }}><input className="num-input" type="number" value={edit.durata_secondi ?? s.durata_secondi} onChange={e => setSerieEditData(p => ({ ...p, [s.id]: { ...p[s.id], durata_secondi: +e.target.value } }))} /><div style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center', marginTop: 2 }}>SEC</div></div>
                            ) : (
                              <div style={{ flex: 1 }}><input className="num-input" type="number" value={edit.ripetizioni ?? s.ripetizioni ?? ''} onChange={e => setSerieEditData(p => ({ ...p, [s.id]: { ...p[s.id], ripetizioni: +e.target.value } }))} /><div style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center', marginTop: 2 }}>RIP</div></div>
                            )}
                            {!edit.usaElastico ? (
                              <div style={{ flex: 1 }}><input className="num-input" type="number" value={edit.peso ?? s.peso ?? 0} onChange={e => setSerieEditData(p => ({ ...p, [s.id]: { ...p[s.id], peso: +e.target.value } }))} /><div style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center', marginTop: 2 }}>KG</div></div>
                            ) : (
                              <div style={{ flex: 2 }}><select className="form-select" style={{ fontSize: 12, padding: '8px' }} value={edit.elastico ?? s.elastico ?? ''} onChange={e => setSerieEditData(p => ({ ...p, [s.id]: { ...p[s.id], elastico: e.target.value } }))}><option value="">-- colore --</option>{['Giallo (leggero)', 'Verde', 'Rosso', 'Blu', 'Viola', 'Nero (forte)'].map(c => <option key={c}>{c}</option>)}</select><div style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center', marginTop: 2 }}>ELASTICO</div></div>
                            )}
                          </div>
                          {blocchi.flatMap(b => b.esercizi || []).find(e => (e.nomeEsercizio || e.nome) === s.esercizio_nome)?.consenteElastico && (
                            <button onClick={() => setSerieEditData(p => ({ ...p, [s.id]: { ...p[s.id], usaElastico: !edit.usaElastico } }))} style={{ background: 'none', border: `1px solid ${edit.usaElastico ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 8, color: edit.usaElastico ? 'var(--accent)' : 'var(--muted)', fontSize: 11, padding: '3px 10px', cursor: 'pointer', alignSelf: 'flex-start' }}>🦺 {edit.usaElastico ? 'Usa kg invece' : 'Usa elastico'}</button>
                          )}
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => aggiornaSerie(s.id)}>Salva</button>
                            <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => setSerieInModifica(null)}>Annulla</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Blocchi e esercizi */}
          {blocchi.map((blocco, bIdx) => {
            const cf = coloreFormato(blocco.formato)
            return (
            <div key={bIdx} className="card" style={stileCardBlocco(blocco.formato)}>
              <IntestazioneBlocco numero={bIdx + 1} blocco={blocco} azione={
                /* Il formato Classico non ha una scaletta a tempo: usa il recupero tra serie, non il timer a schermo intero */
                blocco.formato !== 'classico' && (blocco.esercizi || []).length > 0 ? (
                  <button onClick={() => setTimerBlocco(blocco)}
                    style={{ background: 'var(--bg)', border: `1px solid ${cf.testo}`, borderRadius: 10, padding: '7px 12px', color: cf.testo, fontFamily: 'Syne', fontWeight: 700, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    ⏱️ Timer
                  </button>
                ) : null
              } />
              <div style={{ padding: '0 16px' }}>
                {(blocco.esercizi || []).map((ex, eIdx) => {
                  const logKey = `${bIdx + 1}_${ex.nome}`
                  const log = logData[logKey] || {}
                  const ultimoCarico = ex.esercizioId ? ultimiCarichi[ex.esercizioId] : null
                  const usaElastico = log.usaElastico || false
                  // Lo stesso calcolo che userà salvaSerie: i campi sotto sono
                  // CONTROLLATI su questo valore, quindi quello che si vede è
                  // per costruzione quello che va a database.
                  const carico = caricoDi(log, ultimoCarico)
                  const serieEx = serieDi(bIdx + 1, ex.nome)
                  const previste = seriePreviste(blocco, ex)
                  const fatte = serieEx.length
                  // Con un numero di serie previsto comanda il conteggio: l'esercizio
                  // è finito quando le serie ci sono tutte, non dopo la prima.
                  const isCompletato = previste ? fatte >= previste : exCompletati.has(logKey)
                  const aTempo = ex.tipo_misura === 'tempo'
                  const secondiDiSerie = aTempo ? secondiSerie(blocco, ex) : null
                  const inSalvataggio = salvandoSerie === logKey
                  return (
                    <div key={eIdx} style={{ padding: '12px 0', borderBottom: eIdx < (blocco.esercizi || []).length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{ex.nomeEsercizio || ex.nome}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                            {aTempo
                              ? `${previste ? `${previste}× ` : ''}${secondiDiSerie}s`
                              : `${previste || 1}×${ex.ripetizioni || '?'}`}
                            {ex.recupero ? ` · ${ex.recupero}s rec` : ''}
                          </div>
                          {ex.esercizioId && videoPersonali[ex.esercizioId] && (
                            <a href={videoPersonali[ex.esercizioId]} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                              style={{ color: 'var(--accent2)', fontSize: 11, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                              ▶ il mio video
                            </a>
                          )}
                        </div>
                        {(ex.varianti?.length > 0 || ex.regressioni?.length > 0) && !isCompletato && (
                          <select className="form-select" style={{ width: 'auto', fontSize: 11, padding: '4px 8px', maxWidth: 130 }}
                            value={log.variante || ''}
                            onChange={e => setLog(logKey, 'variante', e.target.value)}>
                            <option value="">Standard</option>
                            {[...comeLista(ex.varianti), ...comeLista(ex.regressioni)].map(v => <option key={v.id} value={v.nome}>{v.nome}</option>)}
                          </select>
                        )}
                      </div>
                      {ex.note && <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8, fontStyle: 'italic' }}>💡 {ex.note}</div>}

                      {/* Countdown di recupero (formato Classico), avviato dopo il salvataggio della serie */}
                      {recuperoAttivo?.logKey === logKey && (
                        <TimerRecupero
                          secondi={recuperoAttivo.secondi}
                          onFine={() => setRecuperoAttivo(null)}
                          onChiudi={() => setRecuperoAttivo(null)}
                        />
                      )}

                      {/* Esercizio finito: quante serie, e niente da aggiungere se il
                          coach ne ha previste un numero preciso. */}
                      {isCompletato && risultatoAperto && !risultatoAperto.completata ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.25)', borderRadius: 10, padding: '8px 12px' }}>
                          <div style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600 }}>
                            {previste
                              ? `✓ ${fatte} di ${previste} serie`
                              : `✓ ${fatte} ${fatte === 1 ? 'serie salvata' : 'serie salvate'}`}
                          </div>
                          {!previste && (
                            <button onClick={() => setExCompletati(prev => { const n = new Set(prev); n.delete(logKey); return n })}
                              style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px', fontSize: 11, color: 'var(--muted)', cursor: 'pointer' }}>
                              + Aggiungi serie
                            </button>
                          )}
                        </div>
                      ) : risultatoAperto && !risultatoAperto.completata ? (
                        <>
                          {/* Quante ne restano: prima, dopo la prima serie, l'esercizio
                              diventava verde e le altre due previste sparivano dalla vista. */}
                          {previste && (
                            <div style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 700, fontFamily: 'Syne', marginBottom: 8 }}>
                              Serie {fatte + 1} di {previste}
                            </div>
                          )}
                          {ultimoCarico && (
                            <div style={{ fontSize: 11, color: 'var(--accent2)', background: 'rgba(255,200,0,0.08)', borderRadius: 8, padding: '5px 10px', marginBottom: 8 }}>
                              📊 Ultima volta: {ultimoCarico.elastico ? `elastico ${ultimoCarico.elastico}` : ultimoCarico.peso > 0 ? `${ultimoCarico.peso}kg` : '0kg'}
                              {ultimoCarico.ripetizioni ? ` · ${ultimoCarico.ripetizioni} rip` : ''}
                            </div>
                          )}
                          {/* Esercizio a tempo: si registra SOLO eseguendolo.
                              Niente campo da riempire e niente spunta da premere —
                              si avvia il cronometro, e a fine countdown la serie è
                              già salvata con la durata davvero eseguita. */}
                          {aTempo ? (
                            <button className="btn btn-primary" style={{ width: '100%', padding: '14px', fontSize: 15, opacity: inSalvataggio ? 0.6 : 1 }}
                              disabled={inSalvataggio}
                              onClick={() => setTimerSerie({
                                logKey,
                                bloccoNum: bIdx + 1,
                                bloccoNome: blocco.nome,
                                esercizio: ex,
                                blocco,
                                secondi: secondiDiSerie,
                                serieNumero: fatte + 1,
                                seriePreviste: previste,
                              })}>
                              ▶ Avvia serie {fatte + 1}{previste ? ` di ${previste}` : ''} · {secondiDiSerie}s
                            </button>
                          ) : (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                            <div style={{ flex: 1 }}>
                              <input className="num-input" type="number" value={log.rip ?? ex.ripetizioni ?? ''} onChange={e => setLog(logKey, 'rip', +e.target.value)} style={{ fontSize: 16 }} />
                              <div style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center', marginTop: 2 }}>RIP</div>
                            </div>
                            {!usaElastico ? (
                              <div style={{ flex: 1 }}>
                                <input className="num-input" type="number" value={carico.peso} onChange={e => setLog(logKey, 'kg', +e.target.value)} style={{ fontSize: 16 }} />
                                <div style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center', marginTop: 2 }}>KG</div>
                              </div>
                            ) : (
                              <div style={{ flex: 2 }}>
                                <select className="form-select" style={{ fontSize: 12, padding: '8px' }} value={carico.elastico || ''} onChange={e => setLog(logKey, 'elastico', e.target.value)}>
                                  <option value="">-- colore --</option>
                                  {['Giallo (leggero)', 'Verde', 'Rosso', 'Blu', 'Viola', 'Nero (forte)'].map(c => <option key={c}>{c}</option>)}
                                </select>
                                <div style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center', marginTop: 2 }}>ELASTICO</div>
                              </div>
                            )}
                            <button className="btn btn-primary" style={{ width: 44, height: 44, padding: 0, borderRadius: 12, fontSize: 18, flexShrink: 0, opacity: inSalvataggio ? 0.6 : 1 }}
                              disabled={inSalvataggio}
                              onClick={() => salvaSerie(bIdx + 1, blocco.nome, ex, blocco)}>{inSalvataggio ? '⏳' : '✓'}</button>
                          </div>
                          )}
                          {!aTempo && ex.consenteElastico && (
                            <button onClick={() => setLog(logKey, 'usaElastico', !usaElastico)}
                              style={{ background: 'none', border: `1px solid ${usaElastico ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 8, color: usaElastico ? 'var(--accent)' : 'var(--muted)', fontSize: 11, padding: '4px 10px', cursor: 'pointer', marginTop: 6 }}>
                              🦺 {usaElastico ? 'Usa kg invece' : 'Uso elastico di aiuto'}
                            </button>
                          )}
                        </>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            </div>
            )
          })}

        </div>
        {conferma && <ModaleConferma {...conferma} onAnnulla={() => setConferma(null)} />}
        {toast && <div className="toast">{toast}</div>}
      </div>
    )
  }

  if (vistaProgressi) return (
    <ProgressiAtleta
      progressiExSel={progressiExSel}
      setProgressiExSel={setProgressiExSel}
      progressiStorico={progressiStorico}
      datiGrafico={datiGrafico}
      tracking={tracking}
      setVistaProgressi={setVistaProgressi}
      setMostraNotifiche={setMostraNotifiche}
      setMostraLibreria={setMostraLibreria}
      setLogData={setLogData}
      apriEsercizioProgressi={apriEsercizioProgressi}
      onCambiaTab={onCambiaTab}
    />
  )

  if (mostraLibreria) return (
    <LibreriaEsercizi
      modalitaScelta={true}
      onScelto={ex => { setMostraLibreria(false); apriEsercizioProgressi(ex) }}
      onChiudi={() => setMostraLibreria(false)}
    />
  )

  if (caricamento) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 32, animation: 'pulse 1.5s infinite' }}>💪</div>
      <div style={{ color: 'var(--muted)', fontSize: 14 }}>Caricamento...</div>
    </div>
  )

  return (
    <>
      <div className="topbar" style={{ display: 'grid', gridTemplateColumns: '40px 1fr auto', alignItems: 'center' }}>
        <div>
          {tab === 'messaggi' && (
            <button onClick={() => setTab('oggi')} className="back-btn" aria-label="Indietro">←</button>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}><div className="logo"><svg viewBox="0 0 226 38" width="178" height="32" xmlns="http://www.w3.org/2000/svg" style={{display:"block"}}>
  <circle cx="14" cy="25" r="10" fill="none" stroke="var(--accent)" strokeWidth="3.5"/>
  <circle cx="24" cy="25" r="10" fill="none" stroke="var(--accent)" strokeWidth="3.5"/>
  <text x="41" y="30" fontFamily="Syne, sans-serif" fontWeight="800" fontSize="19" fill="var(--text)">Fit</text>
  <text x="87" y="30" fontFamily="Syne, sans-serif" fontWeight="800" fontSize="19" fill="var(--accent)">Progress</text>
</svg></div></div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setMostraNotifiche(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: 4 }}>
            <span style={{ fontSize: 20 }}>🔔</span>
            {notificheNonLette > 0 && (
              <span style={{ position: 'absolute', top: 0, right: 0, background: 'var(--accent)', color: '#000', borderRadius: '50%', width: 16, height: 16, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {notificheNonLette > 9 ? '9+' : notificheNonLette}
              </span>
            )}
          </button>
          <button onClick={esci} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 13 }}>Esci</button>
        </div>
      </div>

      <div className="scroll-area fade-in">

        {/* ── HOME ── */}
        {tab === 'oggi' && <>
          <div style={{ paddingTop: 4 }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'capitalize' }}>{oggi}</div>
            <div style={{ fontSize: 18, marginTop: 2, fontFamily: 'Syne', fontWeight: 700, lineHeight: 1.2 }}>
              Ciao, {profilo?.piano === 'coach' ? <span style={{ color: 'var(--accent2)' }}>coach </span> : ''}<span style={{ color: 'var(--accent)' }}>{profilo?.nome}</span> 💪
            </div>
          </div>

          {/* Banner installazione PWA */}
          {pwaPrompt && !pwaDismissed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(127,214,61,0.08)', border: '1px solid rgba(127,214,61,0.25)', borderRadius: 14, padding: '12px 14px' }}>
              <div style={{ fontSize: 28, flexShrink: 0 }}>📲</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 13 }}>Installa FitProgress</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Aggiungila alla schermata home per accesso rapido</div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button onClick={() => { setPwaDismissed(true); localStorage.setItem('pwa_dismissed', '1') }}
                  style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18, padding: '2px 6px' }}>✕</button>
                <button onClick={async () => {
                  pwaPrompt.prompt()
                  const { outcome } = await pwaPrompt.userChoice
                  setPwaPrompt(null)
                  if (outcome === 'accepted') localStorage.setItem('pwa_dismissed', '1')
                }} className="btn btn-primary btn-sm" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                  Installa
                </button>
              </div>
            </div>
          )}

          {/* Banner notifiche push — compare solo dopo che il banner PWA è stato chiuso/installato o non è disponibile,
              per non chiedere due permessi in sequenza al primo secondo */}
          {(pwaDismissed || !pwaPrompt) && pushSupportato() && statoPermessoPush() !== 'denied' && !notifPushAttivo && !notifPushDismissed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(87,184,255,0.08)', border: '1px solid rgba(87,184,255,0.25)', borderRadius: 14, padding: '12px 14px' }}>
              <div style={{ fontSize: 28, flexShrink: 0 }}>🔔</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 13 }}>Attiva le notifiche</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                  {profilo?.piano === 'coach'
                    ? 'Sappi subito quando un atleta entra nel gruppo o risponde'
                    : 'Sappi subito quando il coach programma un allenamento'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button onClick={() => { setNotifPushDismissed(true); localStorage.setItem('notif_push_dismissed', '1') }}
                  style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18, padding: '2px 6px' }}>✕</button>
                <button onClick={async () => {
                  const ris = await abilitaNotifichePush(profilo.id)
                  if (ris.ok) { setNotifPushAttivo(true) }
                  else { setNotifPushDismissed(true); localStorage.setItem('notif_push_dismissed', '1') }
                }} className="btn btn-primary btn-sm" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                  Attiva
                </button>
              </div>
            </div>
          )}

          {/* Coach senza gruppi: la home mostrava solo contatori a zero e nessuna indicazione
              su cosa fare. L'unico pulsante vistoso era "Installa" della PWA. */}
          {profilo?.piano === 'coach' && gruppiCreatiCount === 0 && (
            <div onClick={() => onCambiaTab && onCambiaTab('gruppi')}
              style={{ cursor: 'pointer', background: 'linear-gradient(135deg, rgba(127,214,61,0.14), rgba(127,214,61,0.05))', border: '1px solid rgba(127,214,61,0.35)', borderRadius: 16, padding: '16px 18px' }}>
              <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 16 }}>👥 Crea il tuo primo gruppo</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, lineHeight: 1.45 }}>
                È il punto di partenza: crei un gruppo, inviti i tuoi atleti e programmi gli allenamenti.
              </div>
              <div style={{ marginTop: 10, fontFamily: 'Syne', fontWeight: 700, fontSize: 13, color: 'var(--accent)' }}>Inizia ›</div>
            </div>
          )}

          {/* Riquadri navigazione */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {/* Card 1: I miei gruppi — SOLO COACH */}
            {profilo?.piano === 'coach' && (
              <div onClick={() => onCambiaTab && onCambiaTab('gruppi')} className="card" style={{ cursor: 'pointer' }}>
                <div className="card-body" style={{ padding: '14px 12px' }}>
                  <div style={{ fontSize: 22, marginBottom: 6 }}>👥</div>
                  <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 12, marginBottom: 4 }}>Gruppi · coach</div>
                  <div style={{ fontSize: 30, fontFamily: '"Instrument Sans", sans-serif', fontWeight: 600, color: 'var(--accent)', lineHeight: 1.05 }}>{gruppiCreatiCount}</div>
                  <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>{gruppiCreatiCount === 1 ? 'gruppo creato' : 'gruppi creati'}</div>
                </div>
              </div>
            )}

            {/* Card 2: Gruppi a cui appartengo — uguale per atleta e coach */}
            <div onClick={() => onCambiaTab && onCambiaTab('gruppiatleta')} className="card" style={{ cursor: 'pointer' }}>
              <div className="card-body" style={{ padding: '14px 12px' }}>
                <div style={{ fontSize: 22, marginBottom: 6 }}>👤</div>
                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 12, marginBottom: 4 }}>Gruppi · atleta</div>
                <div style={{ fontSize: 30, fontFamily: '"Instrument Sans", sans-serif', fontWeight: 600, color: 'var(--accent2)', lineHeight: 1.05 }}>{gruppi.length}</div>
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>come atleta</div>
              </div>
            </div>

            {/* Card 3: Progressi */}
            <div onClick={() => onCambiaTab && onCambiaTab('progressiatleta')} className="card" style={{ cursor: 'pointer' }}>
              <div className="card-body" style={{ padding: '14px 12px' }}>
                <div style={{ fontSize: 22, marginBottom: 6 }}>📈</div>
                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 12, marginBottom: 4 }}>Progressi</div>
                <div style={{ fontSize: 30, fontFamily: '"Instrument Sans", sans-serif', fontWeight: 600, color: 'var(--green)', lineHeight: 1.05 }}>{eserciziFavoriti.length}</div>
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>monitorati</div>
              </div>
            </div>

            {/* Card 4: Allenati da solo */}
            <div onClick={() => apriSessPersonale({ tipo: 'nuova' })} className="card" style={{ cursor: 'pointer' }}>
              <div className="card-body" style={{ padding: '14px 12px' }}>
                <div style={{ fontSize: 22, marginBottom: 6 }}>➕</div>
                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 12, marginBottom: 4 }}>Allenati</div>
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3, lineHeight: 1.4 }}>
                  {(() => {
                  const oggi2 = new Date().toISOString().split('T')[0]
                  const spOggi = sessPersonali.filter(s => s.data === oggi2)
                  if (spOggi.length === 0) return 'da solo'
                  return spOggi.every(s => s.completata) ? '+ nuova sessione' : 'in corso →'
                })()}
                </div>
              </div>
            </div>
          </div>

          {/* ── ELENCO UNIFICATO SESSIONI ── */}
          {(() => {
            // Costruisci un unico elenco unificato:
            // - personali (atleta da solo): da fare oggi (non completate) + storico (completate)
            // - atleta in gruppo: prossime (non ancora fatte) + storico (mie completate)
            // - coach: prossime nei miei gruppi + storico nei miei gruppi
            const oggi2 = new Date().toISOString().split('T')[0]

            const personali = sessPersonali.map(sp => ({
              key: 'p' + sp.id,
              tipo: 'personale',
              tipoLabel: 'Personale',
              tipoIcona: '➕',
              tipoColore: 'var(--accent)',
              tipoBg: 'rgba(127,214,61,0.04)',
              tipoBorder: 'rgba(127,214,61,0.2)',
              nome: sp.nome || 'Sessione personale',
              data: sp.data,
              dataKey: sp.completata_il || sp.data,
              fatta: !!sp.completata,
              isOggi: sp.data === oggi2 && !sp.completata,
              azione: sp.completata
                ? () => apriSessPersonale({ tipo: 'storico', sessione: sp })
                : () => apriSessPersonale({ tipo: 'riprendi', sessione: sp }),
            }))

            const atletaProssime = prossimeSessioniTutti.map(s => ({
              key: 'ag' + s.id,
              tipo: 'atleta_gruppo',
              tipoLabel: 'Gruppo (atleta)',
              tipoIcona: '👤',
              tipoColore: 'var(--accent2)',
              tipoBg: 'rgba(87,184,255,0.04)',
              tipoBorder: 'rgba(87,184,255,0.25)',
              nome: s.sessioni_tipo?.nome || 'Sessione',
              sottotitolo: s.nomeGruppo,
              data: s.data,
              dataKey: s.data,
              fatta: false,
              isOggi: s.data === oggi2,
              // Sessione da fare: se è oggi inizia direttamente, altrimenti apre dettaglio gruppo su tab Prossime
              azione: s.data === oggi2
                ? () => caricaSessioneSpecifica(s.id)
                : () => {
                    const g = { id: s.gruppo_id, nome: s.nomeGruppo }
                    setTabGruppo('prossime')
                    caricaDettaglioGruppo(g)
                    setVistaGruppi(true)
                  },
            }))

            const atletaStorico = storicoSessioniTutti
              .filter(s => s.mioRisultato?.completata)
              .map(s => ({
                key: 'as' + s.id,
                tipo: 'atleta_gruppo',
                tipoLabel: 'Gruppo (atleta)',
                tipoIcona: '👤',
                tipoColore: 'var(--accent2)',
                tipoBg: 'rgba(87,184,255,0.03)',
                tipoBorder: 'rgba(87,184,255,0.15)',
                nome: s.sessioni_tipo?.nome || 'Sessione',
                sottotitolo: s.nomeGruppo,
                data: s.data,
                dataKey: s.data,
                fatta: true,
                isOggi: false,
                azione: () => apriSessioneDettaglio(s, s.mioRisultato?.id),
              }))

            const coachProssime = prossimeSessioniCoach.map(s => ({
              key: 'cp' + s.id,
              tipo: 'coach',
              tipoLabel: 'Gruppo (coach)',
              tipoIcona: '🎯',
              tipoColore: 'var(--accent3)',
              tipoBg: 'rgba(251,191,36,0.04)',
              tipoBorder: 'rgba(251,191,36,0.25)',
              nome: s.sessioni_tipo?.nome || 'Sessione',
              sottotitolo: s.nomeGruppo,
              data: s.data,
              dataKey: s.data,
              fatta: false,
              isOggi: s.data === oggi2,
              // Apre direttamente il gruppo giusto sul tab calendario, invece della lista generica
              azione: () => {
                sessionStorage.setItem('fp_apri_gruppo', JSON.stringify({ id: s.gruppo_id, nome: s.nomeGruppo, tab: 'calendario' }))
                onCambiaTab && onCambiaTab('gruppi')
              },
              soloVisualizzazione: true,
            }))

            const coachStorico = storicoSessioniCoach.map(s => ({
              key: 'cs' + s.id,
              tipo: 'coach',
              tipoLabel: 'Gruppo (coach)',
              tipoIcona: '🎯',
              tipoColore: 'var(--accent3)',
              tipoBg: 'rgba(251,191,36,0.03)',
              tipoBorder: 'rgba(251,191,36,0.15)',
              nome: s.sessioni_tipo?.nome || 'Sessione',
              sottotitolo: s.nomeGruppo,
              data: s.data,
              dataKey: s.data,
              fatta: true,
              isOggi: false,
              azione: () => {
                sessionStorage.setItem('fp_apri_gruppo', JSON.stringify({ id: s.gruppo_id, nome: s.nomeGruppo, tab: 'calendario' }))
                onCambiaTab && onCambiaTab('gruppi')
              },
              soloVisualizzazione: true,
            }))

            const tutte = [...personali, ...atletaProssime, ...atletaStorico, ...coachProssime, ...coachStorico]
              .sort((a, b) => {
                // priorità: oggi/da fare prima, poi per data desc
                if (a.isOggi && !b.isOggi) return -1
                if (!a.isOggi && b.isOggi) return 1
                if (!a.fatta && b.fatta) return -1
                if (a.fatta && !b.fatta) return 1
                return new Date(b.dataKey) - new Date(a.dataKey)
              })
              .slice(0, 6)

            if (tutte.length === 0) return (
              <div className="card" style={{ marginTop: 4 }}>
                <div className="card-body" style={{ textAlign: 'center', padding: '20px 16px', fontSize: 13, color: 'var(--muted)' }}>
                  {profilo?.piano === 'coach'
                    ? 'Qui compariranno gli allenamenti che programmi ai tuoi gruppi.'
                    : 'Nessuna sessione ancora. Inizia ad allenarti! 💪'}
                </div>
              </div>
            )

            return (
              <>
                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginTop: 4 }}>
                  Le tue sessioni
                </div>
                {tutte.map(s => (
                  <div key={s.key} className="card" style={{ border: `1px solid ${s.tipoBorder}`, cursor: 'pointer' }}
                    onClick={s.azione}>
                    <div className="card-header" style={{ background: s.tipoBg }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 14 }}>{s.tipoIcona}</span>
                          <div className="card-title" style={{ fontSize: 13 }}>{s.nome}</div>
                          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 7px', borderRadius: 8, background: s.tipoBg, color: s.tipoColore, border: `1px solid ${s.tipoBorder}`, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                            {s.tipoLabel}
                          </span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>
                          {s.sottotitolo ? `${s.sottotitolo} · ` : ''}
                          {s.isOggi
                            ? <span style={{ color: 'var(--accent)', fontWeight: 700 }}>⚡ Oggi</span>
                            : new Date(s.data + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        {s.fatta ? (
                          <span className="badge badge-green" style={{ fontSize: 10 }}>✓ Fatta</span>
                        ) : s.soloVisualizzazione ? (
                          <span className="badge badge-accent" style={{ fontSize: 10 }}>Programmata</span>
                        ) : (
                          <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); s.azione() }}>
                            ▶ Inizia
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )
          })()}

        </>}

        {/* ── MESSAGGI ── */}
        {tab === 'messaggi' && <>
          <div>
            <h2 style={{ fontSize: 20 }}>Messaggi</h2>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>{messaggi.length} messaggi</div>
          </div>
          {messaggi.length === 0 ? (
            <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: '30px', fontSize: 13, color: 'var(--muted)' }}>Nessun messaggio ancora</div></div>
          ) : messaggi.map(m => (
            <div key={m.id} style={{ display: 'flex', gap: 10 }}>
              <div className="avatar" style={{ background: 'rgba(127,214,61,0.15)', color: 'var(--accent)', flexShrink: 0 }}>{m.profili?.nome?.[0]}{m.profili?.cognome?.[0]}</div>
              <div style={{ flex: 1 }}>
                <div style={{ background: 'var(--surface)', borderRadius: '0 12px 12px 12px', padding: '12px 14px', border: '1px solid var(--border)', fontSize: 13, lineHeight: 1.6 }}>{m.testo}</div>
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>
                  {m.profili?.nome} · {new Date(m.creato_il).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}
        </>}

      </div>
      {conferma && <ModaleConferma {...conferma} onAnnulla={() => setConferma(null)} />}
      {toast && <div className="toast">{toast}</div>}
    </>
  )
}
